import { CreateReviewsProps } from '@components/Reviews/Reviews.js';
import { CreateReviewsMiniProps } from '@components/ReviewsMini/ReviewsMini.js';
import { CreateStarsComponentProps } from '@components/Stars/Stars.js';
import { CreateStarsFullComponentProps } from '@components/StarsFull/StarsFull';
import { CreateStarsFullMarkComponentProps } from '@components/StarsFullMark/StarsFullMark.js';
import { CreateStarsFullReviewComponentProps } from '@components/StarsFullReview/StarsFullReview.js';
import { CreateStarsFullThreeReviewsComponentProps } from '@components/StarsFullThreeReviews/StarsFullThreeReviews.js';
import { CreateMicroReviewsProps } from '@components/MicroReviews/MicroReviews.js';
import { CreateMicroTrustscopeProps } from '@components/MicroTrustscore/MicroTrustscore.js';
import { CreateMicroComboProps } from '@components/MicroCombo/MicroCombo.js';

export enum Widgets {
  Reviews = 'Reviews',
  ReviewsMini = 'ReviewsMini',
  ReviewsMicro = 'ReviewsMicro',
  Stars = 'Stars',
  StarsFull = 'StarsFull',
  StarsFullMark = 'StarsFullMark',
  StarsFullReview = 'StarsFullReview',
  StarsFullThreeReviews = 'StarsFullThreeReviews',
  MicroTrustscore = 'MicroTrustscore',
  MicroCombo = 'MicroCombo',
}

export const placeholdersMap = {
  [Widgets.Reviews]: 'tu-widget-reviews',
  [Widgets.ReviewsMini]: 'tu-widget-reviews-mini',
  [Widgets.ReviewsMicro]: 'tu-widget-reviews-micro',
  [Widgets.MicroTrustscore]: 'tu-widget-micro-trustscope',
  [Widgets.MicroCombo]: 'tu-widget-micro-combo',
  [Widgets.Stars]: 'tu-widget-stars',
  [Widgets.StarsFull]: 'tu-widget-stars-full',
  [Widgets.StarsFullMark]: 'tu-widget-stars-fullmark',
  [Widgets.StarsFullReview]: 'tu-widget-stars-fullreview',
  [Widgets.StarsFullThreeReviews]: 'tu-widget-stars-full-threereview',
};

export enum WidgetVariants {
  Light = 'light',
  Dark = 'dark',
}

export interface WidgetVariantMap {
  light: string;
  dark: string;
}

export const widgetVariants: { [key in Widgets]: WidgetVariantMap } = {
  [Widgets.Reviews]: {
    light: 'widget-button-view-1',
    dark: 'widget-button-view-2',
  },
  [Widgets.ReviewsMini]: {
    light: 'widget-mini-view-1',
    dark: 'widget-mini-view-2',
  },
  [Widgets.Stars]: {
    light: 'widget-main-compact-row-view-1',
    dark: 'widget-main-compact-row-view-2',
  },
  [Widgets.StarsFull]: {
    light: 'widget-main-compact-column-view-1',
    dark: 'widget-main-compact-column-view-2',
  },
  [Widgets.StarsFullMark]: {
    light: 'widget-main-full-view-1',
    dark: 'widget-main-full-view-2',
  },
  [Widgets.StarsFullReview]: {
    light: 'widget-review-compact-view-1',
    dark: 'widget-review-compact-view-2',
  },
  [Widgets.StarsFullThreeReviews]: {
    light: 'widget-main-compact-column-view-1',
    dark: 'widget-main-compact-column-view-2',
  },
  [Widgets.ReviewsMicro]: {
    light: 'widget-micro-review-count-view-1',
    dark: 'widget-micro-review-count-view-2',
  },
  [Widgets.MicroTrustscore]: {
    light: 'widget-micro-trustscore-view-1',
    dark: 'widget-micro-trustscore-view-2',
  },
  [Widgets.MicroCombo]: {
    light: 'widget-micro-combo-view-1',
    dark: 'widget-micro-combo-view-2',
  },
};

export const widgetsMap = {
  [Widgets.Reviews]: () => import('@components/Reviews/Reviews.js'),
  [Widgets.ReviewsMini]: () => import('@components/ReviewsMini/ReviewsMini.js'),
  [Widgets.ReviewsMicro]: () => import('@components/MicroReviews/MicroReviews.js'),
  [Widgets.MicroTrustscore]: () => import('@components/MicroTrustscore/MicroTrustscore.js'),
  [Widgets.MicroCombo]: () => import('@components/MicroCombo/MicroCombo.js'),
  [Widgets.Stars]: () => import('@components/Stars/Stars.js'),
  [Widgets.StarsFull]: () => import('@components/StarsFull/StarsFull.js'),
  [Widgets.StarsFullMark]: () => import('@components/StarsFullMark/StarsFullMark.js'),
  [Widgets.StarsFullReview]: () => import('@components/StarsFullReview/StarsFullReview.js'),
  [Widgets.StarsFullThreeReviews]: () => import('@components/StarsFullThreeReviews/StarsFullThreeReviews.js'),
};

type WidgetCreateFunctionMap = {
  [Widgets.Reviews]: (props: CreateReviewsProps) => HTMLElement;
  [Widgets.ReviewsMini]: (props: CreateReviewsMiniProps) => HTMLElement;
  [Widgets.ReviewsMicro]: (props: CreateMicroReviewsProps) => HTMLElement;
  [Widgets.MicroTrustscore]: (props: CreateMicroTrustscopeProps) => HTMLElement;
  [Widgets.MicroCombo]: (props: CreateMicroComboProps) => HTMLElement;
  [Widgets.Stars]: (props: CreateStarsComponentProps) => HTMLElement;
  [Widgets.StarsFull]: (props: CreateStarsFullComponentProps) => HTMLElement;
  [Widgets.StarsFullMark]: (props: CreateStarsFullMarkComponentProps) => HTMLElement;
  [Widgets.StarsFullReview]: (props: CreateStarsFullReviewComponentProps) => HTMLElement;
  [Widgets.StarsFullThreeReviews]: (props: CreateStarsFullThreeReviewsComponentProps) => HTMLElement;
};

export type WidgetModule = {
  [K in keyof WidgetCreateFunctionMap as `create${Capitalize<K>}Component`]: WidgetCreateFunctionMap[K];
} & {
  Variants: {
    Light: string;
    Dark: string;
  };
};

export type WidgetProps<T extends Widgets> = T extends Widgets.Reviews
  ? CreateReviewsProps
  : T extends Widgets.ReviewsMini
    ? CreateReviewsMiniProps
    : T extends Widgets.ReviewsMicro
      ? CreateMicroReviewsProps
      : T extends Widgets.MicroTrustscore
        ? CreateMicroTrustscopeProps
        : T extends Widgets.MicroCombo
          ? CreateMicroComboProps
          : T extends Widgets.Stars
            ? CreateStarsComponentProps
            : T extends Widgets.StarsFull
              ? CreateStarsFullComponentProps
              : T extends Widgets.StarsFullMark
                ? CreateStarsFullMarkComponentProps
                : T extends Widgets.StarsFullReview
                  ? CreateStarsFullReviewComponentProps
                  : T extends Widgets.StarsFullThreeReviews
                    ? CreateStarsFullThreeReviewsComponentProps
                    : never;
